import React from 'react';
import {
  Container,
  BackgroundContainer,
  Line,
  InfoBox,
  DetailsBox,
  Label,
  Value,
  DetailsItem,
  ButtonsBox,
  ButtonContainer,
  CancelButton,
  ButtonItem,
  StatusText,
  SavingIndicator,
  DesktopStatus,
  OptionalButton,
  SaveTransction,
  CancelTransactionButton,
} from '@app/components/header-secondary/header-secondary-components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button } from '@app/components';
import { primaryColor } from '@app/styles';
import {
  faArrowLeft,
  faCircleNotch,
  faEllipsisH,
} from '@fortawesome/free-solid-svg-icons';
import StatusBar from '@app/components/status-bar';
import {
  MobileDropdown,
  MobileDropdownOption,
  MobileHeader,
  MobileOptions,
} from '@app/modules/transaction-details/transactions-details-components';
const ArrowStyle = {
  cursor: 'pointer',
};

type HeaderProps = {
  onBackClick?: (e: any) => void;
  showLabel?: boolean;
  dataLabel?: string;
  title: string;
  statusColor?: 'primary' | 'secondary' | 'magento' | 'success';
  statusValue?: string;
  showStatus?: boolean;
  showText?: boolean;
  text?: string;
  showButton?: boolean;
  showOptionalButton?: boolean;
  secondaryButtonText?: string;
  optionalButtonText?: string;
  onSecondaryButtonClick?: (e: any) => void;
  onOptionalButtonClick?: (e: any) => void;
  onPrimaryButtonClick?: (e: any) => void;
  onApproveButtonClick?: (e: any) => void;
  onPrimaryMobileButtonClick?: (e: any) => void;
  primaryButtonText?: string;
  primaryMobileButtonText?: string;
  disabledPrimaryButton?: boolean;
  disabledMobilePrimaryButton?: boolean;
  saving?: boolean;
  showPrimaryButton?: boolean;
  showMobilePrimaryButton?: boolean;
  variant?: 'primary' | 'secondary' | 'text' | 'warning';
  showSecondaryBackground?: boolean;
  approveButton?: string;
  showApproveBackground?: boolean;
  showApproveButton?: boolean;
  showMenuBar?: boolean;
  saveTransactionButton?: string;
  cancelTransactionButton?: string;
  showTransactionSaveButton?: boolean;
  showCancelButton?: boolean;
};

export default function SecondaryHeader({
  onBackClick,
  showLabel,
  dataLabel,
  title,
  statusColor,
  statusValue,
  showStatus,
  showText,
  text,
  showButton,
  showOptionalButton,
  approveButton,
  showApproveButton,
  onSecondaryButtonClick,
  onOptionalButtonClick,
  showMenuBar,
  secondaryButtonText,
  optionalButtonText,
  onApproveButtonClick,
  onPrimaryButtonClick,
  onPrimaryMobileButtonClick,
  primaryButtonText,
  primaryMobileButtonText,
  disabledPrimaryButton = false,
  saving,
  showPrimaryButton,
  saveTransactionButton,
  cancelTransactionButton,
  showTransactionSaveButton,
  showCancelButton,
  showMobilePrimaryButton,
}: HeaderProps) {
  const [showSideNav, toggleSideNav] = React.useState(false);
  const sideNavRef: any = React.useRef(null);
  function handleClickOutside(e: any) {
    if (
      showSideNav &&
      sideNavRef.current &&
      !sideNavRef.current.contains(e.target)
    ) {
      toggleSideNav(false);
    }
  }
  document.addEventListener('click', handleClickOutside);
  const MenuIconStyle = {
    height: 50,
    width: 30,
  };
  return (
    <>
      <BackgroundContainer>
        <Container>
          <InfoBox>
            <FontAwesomeIcon
              icon={faArrowLeft}
              onClick={onBackClick}
              size="lg"
              style={ArrowStyle}
            />
            <Line />
            <DetailsBox>
              {showLabel && <Label>{dataLabel}</Label>}
              <DetailsItem>
                <Value>{title}</Value>
                {showStatus && (
                  <DesktopStatus>
                    <StatusBar variant={statusColor}>
                      {statusValue === 'COMPLETED'
                        ? 'REQUESTED FOR APPROVAL'
                        : statusValue}
                    </StatusBar>
                  </DesktopStatus>
                )}
                {showText && <StatusText>{text}</StatusText>}
              </DetailsItem>
            </DetailsBox>
          </InfoBox>
          <ButtonsBox>
            <ButtonContainer>
              {saving && (
                <SavingIndicator>
                  <FontAwesomeIcon
                    color={primaryColor}
                    icon={faCircleNotch}
                    size="lg"
                    spin
                  />
                </SavingIndicator>
              )}
              {showMenuBar ? (
                <MobileHeader ref={sideNavRef}>
                  <FontAwesomeIcon
                    color={'#000000'}
                    icon={faEllipsisH}
                    style={MenuIconStyle}
                    onClick={() => {
                      toggleSideNav(!showSideNav);
                    }}
                  />
                </MobileHeader>
              ) : null}
              {showSideNav ? (
                <MobileDropdown>
                  <MobileOptions>
                    {showButton && (
                      <MobileDropdownOption onClick={onSecondaryButtonClick}>
                        {secondaryButtonText}
                      </MobileDropdownOption>
                    )}
                    {showOptionalButton && (
                      <MobileDropdownOption
                        disabled={
                          optionalButtonText === 'Exporting...' ? true : false
                        }
                        onClick={onOptionalButtonClick}>
                        {optionalButtonText}
                      </MobileDropdownOption>
                    )}
                    {showMobilePrimaryButton && (
                      <MobileDropdownOption
                        onClick={onPrimaryMobileButtonClick}>
                        {primaryMobileButtonText}
                      </MobileDropdownOption>
                    )}
                  </MobileOptions>
                </MobileDropdown>
              ) : null}
              {showButton ? (
                <CancelButton onClick={onSecondaryButtonClick}>
                  {secondaryButtonText}
                </CancelButton>
              ) : null}
              {showOptionalButton ? (
                <OptionalButton>
                  <Button
                    variant="primary"
                    disabled={
                      optionalButtonText === 'Exporting...' ? true : false
                    }
                    onClick={onOptionalButtonClick}>
                    {optionalButtonText}
                  </Button>
                </OptionalButton>
              ) : null}
              {showPrimaryButton ? (
                <ButtonItem disabled={disabledPrimaryButton}>
                  <Button
                    variant={
                      primaryButtonText === 'Approve' ||
                      primaryButtonText === 'Unapprove'
                        ? 'approve'
                        : 'primary'
                    }
                    size="medium"
                    width="100%"
                    type="submit"
                    onClick={onPrimaryButtonClick}>
                    {primaryButtonText}
                  </Button>
                </ButtonItem>
              ) : null}
              {showCancelButton ? (
                <CancelTransactionButton onClick={onSecondaryButtonClick}>
                  {secondaryButtonText}
                </CancelTransactionButton>
              ) : null}
              {showTransactionSaveButton ? (
                <SaveTransction disabled={disabledPrimaryButton}>
                  <Button
                    variant="warning"
                    size="medium"
                    width="100%"
                    type="submit"
                    onClick={onPrimaryButtonClick}>
                    {primaryButtonText}
                  </Button>
                </SaveTransction>
              ) : null}
            </ButtonContainer>
          </ButtonsBox>
        </Container>
      </BackgroundContainer>
    </>
  );
}
